<template>
  <div class="motor-claim">
    <shell>
      <template #appbar>
        <app-bar
          :show-back="screen !== 'done'"
          :subtitle="subtitle"
          :step-label="stepLabel"
          @back="goBack"
        />
      </template>

      <template v-if="railActive" #rail>
        <progress-rail :steps="railSteps" :active-step="railActive" />
      </template>
      <template v-if="railActive" #sidebar>
        <case-file-rail
          :steps="railSteps"
          :active-step="railActive"
          :summaries="stepSummaries"
          @jump="jumpToStep"
        />
      </template>

      <lookup-step
        v-if="screen === 'lookup'"
        :value="S"
        :lookup-failed="lookupFailed"
        @input="merge"
      />
      <ai-interview-step
        v-else-if="screen === 'interview'"
        :sections="AI_SECTIONS"
        :index="S.ai.sec"
        :value="S.ai"
        @input="(ai) => (S.ai = ai)"
        @toast="showToast"
      />
      <what-happened-step
        v-else-if="screen === 'whatHappened'"
        :value="S"
        @input="merge"
        @toast="showToast"
      />
      <other-vehicles-step
        v-else-if="screen === 'otherVehicles'"
        :value="S"
        @input="merge"
        @toast="showToast"
      />
      <people-step
        v-else-if="screen === 'people'"
        :value="S"
        @input="merge"
        @toast="showToast"
      />
      <damage-step v-else-if="screen === 'damage'" :value="S" @input="merge" />
      <documents-step
        v-else-if="screen === 'documents'"
        :docs="DOCS"
        :captured="S.docs"
        @capture="onCapture"
        @remove-capture="onRemoveCapture"
        @capture-error="(key, msg) => showToast(msg)"
      />
      <review-sign-step
        v-else-if="screen === 'review'"
        :groups="reviewGroups"
        :signature="S.sign"
        :consent="S.consent"
        inline-edit
        @edit-opened="onEditOpened"
        @update-signature="(v) => (S.sign = v)"
        @update-consent="(v) => (S.consent = v)"
      >
        <template #editor="{ groupKey }">
          <component
            :is="STEP_FOR[groupKey]"
            compact
            :value="S"
            @input="merge"
            @toast="showToast"
          />
        </template>
      </review-sign-step>
      <done-step
        v-else-if="screen === 'done'"
        :reference="S.ref"
        :phone="S.phone"
      />

      <template #bar>
        <bottom-bar>
          <template v-if="screen === 'done'">
            <button
              type="button"
              class="mc-btn mc-btn--sec"
              @click="showToast('Your claim summary would download here')"
            >
              Save summary
            </button>
            <button
              type="button"
              class="mc-btn mc-btn--pri"
              @click="$emit('back')"
            >
              Done
            </button>
          </template>
          <template v-else>
            <button
              v-if="screen !== 'lookup'"
              type="button"
              class="mc-btn mc-btn--sec"
              @click="goBack"
            >
              Back
            </button>
            <button
              type="button"
              class="mc-btn mc-btn--pri"
              :disabled="
                !canContinue || lookingUp || submittingStep || extracting
              "
              @click="onPrimary"
            >
              {{ primaryLabel }}
            </button>
          </template>
        </bottom-bar>
      </template>

      <template #toast><toast ref="toast" /></template>
    </shell>
  </div>
</template>

<script>
import Shell from './shell/Shell.vue'
import AppBar from './shell/AppBar.vue'
import ProgressRail from './shell/ProgressRail.vue'
import CaseFileRail from './shell/CaseFileRail.vue'
import BottomBar from './shell/BottomBar.vue'
import Toast from './shell/Toast.vue'
import LookupStep from './steps/LookupStep.vue'
import WhatHappenedStep from './steps/WhatHappenedStep.vue'
import OtherVehiclesStep from './steps/OtherVehiclesStep.vue'
import PeopleStep from './steps/PeopleStep.vue'
import DamageStep from './steps/DamageStep.vue'
import DocumentsStep from './shared/DocumentsStep.vue'
import ReviewSignStep from './shared/ReviewSignStep.vue'
import DoneStep from './shared/DoneStep.vue'
import AiInterviewStep from './steps/AiInterviewStep.vue'
import {
  DRAFT_KEYS,
  saveDraft,
  loadDraft,
  clearDraft,
} from '@/utils/claimDraft'
import {
  initiateClaim,
  submitClaim,
  uploadClaimAttachment,
} from '@/services/claim-service'
import { toFullClaimBody } from '@/utils/claimStepMapper'
import { extractInterviewFields } from '@/utils/aiFieldExtraction'

const SCREEN_ORDER = [
  'lookup',
  'whatHappened',
  'otherVehicles',
  'people',
  'damage',
  'documents',
  'review',
]
// Voice path: same lookup step as the typed path (initiateClaim needs a
// regNo/phone from somewhere), then the interview replaces the typed steps,
// then rejoins documents -> review.
const VOICE_SCREEN_ORDER = ['lookup', 'interview', 'documents', 'review']
// Editor for each review group key; all four share the same value/input contract.
const STEP_FOR = {
  whatHappened: WhatHappenedStep,
  otherVehicles: OtherVehiclesStep,
  people: PeopleStep,
  damage: DamageStep,
}
// Rail screens/labels per journey, so jumps only target this journey's screens.
const RAIL = {
  form: {
    screens: SCREEN_ORDER,
    labels: [
      'Look-up',
      'What happened',
      'Other vehicles',
      'People',
      'Damage',
      'Documents',
      'Review',
    ],
  },
  voice: {
    screens: VOICE_SCREEN_ORDER,
    labels: ['Look-up', 'AI interview', 'Documents', 'Review'],
  },
}
const ASSESS = [
  { id: 'a1', t: 'APA Assessment Centre (Nairobi)' },
  { id: 'a5', t: 'At an address I’ll give you' },
]
// `accept` defaults to image/* (see PhotoDocCapture). Only the licence and ID
// are mandatory; every vehicle/damage photo and the abstract/dashcam are optional.
const DOCS = [
  {
    key: 'lic',
    label: 'Driver’s Licence',
    hint: 'The person who was driving',
    required: true,
  },
  { key: 'id', label: 'National ID', required: true },
  { key: 'front', label: 'Vehicle Front Photo', required: false },
  { key: 'rear', label: 'Vehicle Rear Photo', required: false },
  { key: 'left', label: 'Left Side Photo', required: false },
  { key: 'right', label: 'Right Side Photo', required: false },
  { key: 'close', label: 'Close-up Damage Photos', required: false },
  { key: 'abstract', label: 'Police Abstract', required: false },
  { key: 'dash', label: 'Dashcam Video', required: false, accept: 'video/*' },
]

// The AI claims-officer script: six guided sections, each with its prompt (`intro`) and checklist (`asks`).
const AI_SECTIONS = [
  {
    key: 'driver',
    title: 'Driver Information',
    intro:
      "Let's start with who was behind the wheel. Tell me about the driver at the time of the accident.",
    asks: [
      'Full name of the driver',
      'Their age',
      'Whether they hold a valid driving licence',
      'Licence number if you know it',
      'Their relationship to you (self, spouse, brother, employee…)',
      'How long they have been driving',
    ],
  },
  {
    key: 'story',
    title: 'Accident Story',
    intro:
      "Now I'd like you to describe the accident. Please tell the story naturally from beginning to end.",
    asks: [
      'Where the accident happened',
      'Approximate time',
      'Direction you were travelling',
      'How the accident occurred',
      'Weather and road conditions',
      'What happened immediately after',
    ],
  },
  {
    key: 'people',
    title: 'Passengers',
    optional: true, // ungated on the typed path too, so skippable here
    intro:
      'Was anyone else in your vehicle? Tell me about every passenger on board.',
    asks: [
      'How many passengers were in the vehicle',
      'Their names and ages',
      'Whether anyone was injured',
      'Whether anyone was taken to hospital',
    ],
  },
  {
    key: 'other',
    title: 'Other Vehicles',
    optional: true,
    intro:
      'Was another vehicle involved? Tell me what you know about it and its driver.',
    asks: [
      'Registration and make/model of the other vehicle',
      'The other driver’s name and contact',
      'Their insurer if known',
      'Damage to their vehicle',
    ],
  },
  {
    key: 'witness',
    title: 'Witnesses & Police',
    intro: 'Were there any witnesses, or did police attend the scene?',
    asks: [
      'Whether there were any witnesses',
      'Their name and phone number',
      'Whether police attended and which station',
    ],
  },
  {
    key: 'damage',
    title: 'Vehicle Damage',
    intro:
      'Finally, describe the damage to your own vehicle in as much detail as you can.',
    asks: [
      'Which parts of the vehicle are damaged',
      'How severe the damage is',
      'Whether the vehicle is still drivable',
      'Any airbags deployed or pre-existing damage',
    ],
  },
]

const blankState = () => ({
  onBehalf: false,
  reg: '',
  phone: '',
  when: '',
  incidentLocation: { latitude: null, longitude: null, locationName: '' },
  atScene: null,
  story: '',
  surface: '',
  weather: '',
  vis: '',
  light: '',
  police: null,
  station: '',
  ob: '',
  policePhone: '',
  trafficOffenceNumber: '',
  driverWasMe: true,
  otherDriverName: '',
  otherDriverEmail: '',
  driverToBlame: null,
  vehicles: [],
  otherProperty: '',
  people: [],
  witnessName: '',
  witnessPhone: '',
  areas: [],
  severity: '',
  preex: '',
  damageDesc: '',
  assess: '',
  assessAddr: '',
  docs: {},
  sign: '',
  consent: false,
  ref: null,
  // Set once /initiate-claim succeeds. claimId tracks this claim through the
  // rest of the wizard's /step calls; policyNumber is GIS's policy number,
  // shown as a confirmation — it is not the final claim reference, which is
  // only assigned on submission.
  claimId: null,
  policyNumber: null,
  // voice mode only: current section, and section key -> transcript ('' is a real empty answer)
  // ex holds the transcripts already extracted, so a re-run can't clobber later edits
  ai: { sec: 0, t: {}, ex: {} },
})

function nowLocal() {
  const d = new Date()
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}

function fmtWhen(v) {
  if (!v) return ''
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return v
  return (
    d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }) +
    ', ' +
    d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  )
}

function lookupSummary(S) {
  if (!S.reg) return ''
  return S.phone ? `${S.reg} · ${S.phone}` : S.reg
}

function vehiclesSummary(S) {
  if (!S.vehicles.length) return ''
  const suffix = S.vehicles.length > 1 ? 's' : ''
  return `${S.vehicles.length} vehicle${suffix} added`
}

function peopleSummary(S) {
  if (!S.people.length) return ''
  const suffix = S.people.length > 1 ? 's' : ''
  return `${S.people.length} person${suffix} added`
}

function policeSummary(S) {
  if (S.police === null) return ''
  return S.police ? `Yes — ${S.station || 'station not given'}` : 'No'
}

function driverBlameSummary(S) {
  if (S.driverToBlame == null) return '—'
  return S.driverToBlame ? 'Yes' : 'No'
}

function assessedSummary(assessed, S) {
  if (!assessed) return 'Not chosen'
  const addr = S.assess === 'a5' && S.assessAddr ? ` — ${S.assessAddr}` : ''
  return assessed.t + addr
}

function interviewSummary(S) {
  const answered = AI_SECTIONS.filter((section) => {
    const transcript = S.ai.t[section.key]
    return typeof transcript === 'string' && transcript.trim().length > 0
  }).length
  if (!answered) return ''
  return `${answered} of ${AI_SECTIONS.length} sections answered`
}

function docsSummary(S) {
  const n = Object.keys(S.docs).length
  if (!n) return ''
  return `${n} item${n > 1 ? 's' : ''} attached`
}

// Shell for both wizard journeys, typed form and AI interview, selected by `mode`.
// Nothing saves per step: S is drafted locally until review POSTs /api/claims/submit.
export default {
  name: 'MotorClaimForm',
  components: {
    Shell,
    AppBar,
    ProgressRail,
    CaseFileRail,
    BottomBar,
    Toast,
    LookupStep,
    WhatHappenedStep,
    OtherVehiclesStep,
    PeopleStep,
    DamageStep,
    DocumentsStep,
    ReviewSignStep,
    DoneStep,
    AiInterviewStep,
  },
  props: {
    // 'form' = typed wizard, 'voice' = AI interview; both share this shell.
    mode: {
      type: String,
      default: 'form',
      validator: (v) => ['form', 'voice'].includes(v),
    },
  },
  data() {
    return {
      // both journeys start with the same lookup step, since initiateClaim
      // needs a regNo/phone regardless of how the rest is collected
      screen: 'lookup',
      S: blankState(),
      DOCS,
      AI_SECTIONS,
      STEP_FOR,
      // review groups edited in place, re-gated against their own step's rules
      editedGroups: [],
      lookingUp: false,
      // an unsuccessful policy lookup, which shows the back-office card
      lookupFailed: false,
      submittingStep: false,
      extracting: false,
    }
  },
  computed: {
    origin() {
      return this.mode === 'voice' ? 'AUDIO' : 'FORM'
    },
    screenOrder() {
      return this.mode === 'voice' ? VOICE_SCREEN_ORDER : SCREEN_ORDER
    },
    // each journey drafts under its own key; sharing one let a saved voice
    // draft restore its 'interview' screen into the typed path
    draftKey() {
      return this.mode === 'voice'
        ? DRAFT_KEYS.voiceClaim
        : DRAFT_KEYS.motorClaim
    },
    rail() {
      return RAIL[this.mode]
    },
    railSteps() {
      return this.rail.labels
    },
    railActive() {
      // the interview drives its own section trail in the app bar
      if (this.screen === 'done' || this.screen === 'interview') return 0
      const i = this.rail.screens.indexOf(this.screen)
      return i < 0 ? 0 : i + 1
    },
    stepLabel() {
      if (this.screen === 'interview') {
        return `${this.S.ai.sec + 1} / ${AI_SECTIONS.length}`
      }
      const n = this.railActive
      return n ? `Step ${n} of ${this.railSteps.length}` : ''
    },
    subtitle() {
      if (this.screen === 'done') return `Claim ${this.S.ref || ''}`
      if (this.screen === 'interview') {
        return `AI Interview · ${AI_SECTIONS[this.S.ai.sec].title}`
      }
      return 'Motor claim · First Notice of Loss'
    },
    docsOk() {
      return this.DOCS.filter((d) => d.required).every(
        (d) => this.S.docs[d.key] && !this.S.docs[d.key].uploading
      )
    },
    // One-line preview per completed step, keyed by this journey's rail position.
    stepSummaries() {
      const S = this.S
      const summaryFor = {
        lookup: lookupSummary(S),
        whatHappened: S.incidentLocation.locationName || '',
        otherVehicles: vehiclesSummary(S),
        people: peopleSummary(S),
        damage: S.severity ? `${S.severity} damage` : '',
        interview: interviewSummary(S),
        documents: docsSummary(S),
      }
      return this.rail.screens.reduce((acc, screen, i) => {
        acc[i + 1] = summaryFor[screen] || ''
        return acc
      }, {})
    },
    // shared with review, so an inline edit is held to the step's own rule
    whatHappenedOk() {
      const S = this.S
      return (
        !!S.when &&
        S.story.trim().length >= 15 &&
        !!S.incidentLocation.locationName.trim()
      )
    },
    damageOk() {
      const S = this.S
      return (
        S.areas.length > 0 &&
        !!S.severity &&
        S.damageDesc.trim().length >= 10 &&
        !!S.assess &&
        (S.assess !== 'a5' || S.assessAddr.trim().length > 2)
      )
    },
    // Only edited groups are re-gated; the voice journey never visits these steps.
    editedGroupsOk() {
      const RULES = {
        whatHappened: () => this.whatHappenedOk,
        damage: () => this.damageOk,
      }
      return this.editedGroups.every((key) =>
        RULES[key] ? RULES[key]() : true
      )
    },
    canContinue() {
      const S = this.S
      switch (this.screen) {
        case 'interview': {
          const section = AI_SECTIONS[S.ai.sec]
          // skippable here because they're ungated on the typed path too
          if (section.optional) return true
          // answered once a transcript exists — recorded or typed, same place
          const t = S.ai.t[section.key]
          return t !== undefined && t.trim().length > 2
        }
        case 'lookup':
          return (
            S.reg.trim().length >= 5 &&
            /\d{9,}/.test(S.phone.replace(/\D/g, ''))
          )
        case 'whatHappened':
          return this.whatHappenedOk
        case 'damage':
          return this.damageOk
        case 'documents':
          return this.docsOk
        case 'review':
          return S.sign.trim().length >= 4 && S.consent && this.editedGroupsOk
        default:
          return true
      }
    },
    primaryLabel() {
      if (this.screen === 'lookup' && this.lookingUp) return 'Looking up…'
      if (this.screen === 'interview') {
        if (this.extracting) return 'Extracting details…'
        return this.S.ai.sec === AI_SECTIONS.length - 1
          ? 'Continue to documents'
          : 'Next section'
      }
      if (this.submittingStep) return 'Saving…'
      if (this.screen === 'damage') return 'Continue to documents'
      if (this.screen === 'documents') return 'Continue to review'
      if (this.screen === 'review') return 'Submit claim'
      return 'Continue'
    },
    reviewGroups() {
      const S = this.S
      const assessed = ASSESS.find((a) => a.id === S.assess)
      return [
        {
          key: 'whatHappened',
          title: 'What happened',
          rows: [
            ['Date and time', fmtWhen(S.when)],
            ['Location', S.incidentLocation.locationName],
            ['Account', S.story],
            ['Road surface', S.surface],
            ['Weather', S.weather],
            ['Visibility', S.vis],
            ['Lighting', S.light],
            ['Police attended', policeSummary(S)],
            ['OB / constable number', S.police ? S.ob : '—'],
            ['Police telephone number', S.police ? S.policePhone : '—'],
            ['Traffic offence number', S.police ? S.trafficOffenceNumber : '—'],
            ['Who was driving?', S.driverWasMe ? 'Me' : 'Someone else'],
            ['Driver name', S.driverWasMe ? '—' : S.otherDriverName],
            ['Driver email address', S.driverWasMe ? '—' : S.otherDriverEmail],
            ['Driver to blame', driverBlameSummary(S)],
          ],
        },
        {
          key: 'otherVehicles',
          title: 'Other vehicles and property',
          rows: (S.vehicles.length
            ? S.vehicles.map((v, i) => [
                `Vehicle ${i + 1}`,
                [v.reg, v.makeModel, v.owner, v.insurer]
                  .filter(Boolean)
                  .join(' · '),
              ])
            : [['Vehicles added', 'None added']]
          ).concat([['Other property damaged', S.otherProperty || 'None']]),
        },
        {
          key: 'people',
          title: 'People involved',
          rows: (S.people.length
            ? S.people.map((p) => [
                p.name + (p.age ? ` (${p.age})` : ''),
                `${p.role} — ${p.injured}${
                  p.hospital ? ' · ' + p.hospital : ''
                }`,
              ])
            : [['People involved', 'None added']]
          ).concat([
            [
              'Witness',
              [S.witnessName, S.witnessPhone].filter(Boolean).join(' · '),
            ],
          ]),
        },
        {
          key: 'damage',
          title: 'Damage and repair',
          rows: [
            ['Damaged Area', S.areas.join(', ')],
            ['Severity', S.severity],
            ['Pre-existing Damage', S.preex],
            ['Damage notes', S.damageDesc],
            ['Assessed at', assessedSummary(assessed, S)],
          ],
        },
      ]
    },
  },
  watch: {
    S: {
      deep: true,
      handler() {
        // ref means submitted; this flushes after clearDraft, so it would re-save
        if (this.S.ref) return
        saveDraft(this.draftKey, { screen: this.screen, S: this.S })
      },
    },
  },
  created() {
    const draft = loadDraft(this.draftKey)
    if (draft && draft.S) {
      this.S = { ...blankState(), ...draft.S }
      // ignore a screen this journey doesn't have, whatever wrote it
      if (this.screenOrder.includes(draft.screen)) this.screen = draft.screen
    }
    if (!this.S.when) this.S.when = nowLocal()
  },
  methods: {
    merge(patch) {
      Object.assign(this.S, patch)
    },
    onEditOpened(key) {
      if (!this.editedGroups.includes(key)) this.editedGroups.push(key)
    },
    showToast(message) {
      if (this.$refs.toast) this.$refs.toast.show(message)
    },
    async onCapture(key, file) {
      this.S.docs = {
        ...this.S.docs,
        [key]: { name: file.name, uploading: true },
      }
      try {
        const { data } = await uploadClaimAttachment(file, this.S.reg, key)
        if (!data || !data.success) throw new Error(data && data.msg)
        this.S.docs = {
          ...this.S.docs,
          [key]: { name: file.name, type: data.data.type, url: data.data.url },
        }
      } finally {
        // Not caught: a failure just propagates. This only clears a doc
        // left stuck at uploading:true so the tile doesn't stay blocked —
        // it's a no-op on the success path, which already replaced the
        // entry above before finally runs.
        if (this.S.docs[key] && this.S.docs[key].uploading) {
          const docs = { ...this.S.docs }
          delete docs[key]
          this.S.docs = docs
        }
      }
    },
    onRemoveCapture(key) {
      const docs = { ...this.S.docs }
      delete docs[key]
      this.S.docs = docs
    },
    jumpToStep(stepNumber) {
      const target = this.rail.screens[stepNumber - 1]
      if (target) this.screen = target
    },
    goBack() {
      // inside the interview, back steps through sections before leaving it
      if (this.screen === 'interview' && this.S.ai.sec > 0) {
        this.S.ai.sec -= 1
        return
      }
      const i = this.screenOrder.indexOf(this.screen)
      if (i <= 0) this.$emit('back')
      else this.screen = this.screenOrder[i - 1]
    },
    onPrimary() {
      if (this.screen === 'lookup') {
        this.submitLookup()
        return
      }
      if (this.screen === 'review') {
        this.finishClaim()
        return
      }
      if (this.screen === 'interview') {
        if (this.S.ai.sec < AI_SECTIONS.length - 1) {
          this.S.ai.sec += 1
        } else {
          this.finishInterview()
        }
        return
      }
      this.advanceStep()
    },
    // Extraction runs once, on leaving the last section — never blocks the
    // transition to 'documents' even if every call fails, since the
    // verbatim transcripts stay visible in review regardless.
    async finishInterview() {
      this.extracting = true
      try {
        const changes = await extractInterviewFields(this.S)
        // record what was extracted, so a second pass only re-runs re-recorded sections
        this.S = {
          ...this.S,
          ...changes,
          ai: { ...this.S.ai, ex: { ...this.S.ai.t } },
        }
      } catch {
        // extractInterviewFields already isolates per-section failures;
        // this only guards against something unexpected (e.g. a bug in the
        // merge itself) from blocking the user's own progress
      } finally {
        this.extracting = false
      }
      const i = this.screenOrder.indexOf(this.screen)
      this.screen = this.screenOrder[i + 1]
    },
    async submitLookup() {
      this.lookingUp = true
      this.lookupFailed = false
      try {
        const { data } = await initiateClaim(
          this.S.reg.trim(),
          this.S.phone.trim()
        )
        if (!data || !data.success) {
          // no claimId without a policy match, and /claims/submit requires one
          this.lookupFailed = true
          return
        }
        this.S.claimId = data.data.id
        this.S.policyNumber = data.data.claimReference
        this.showToast('Policy verified')
        const i = this.screenOrder.indexOf(this.screen)
        this.screen = this.screenOrder[i + 1]
      } finally {
        this.lookingUp = false
      }
    },
    // Every screen between lookup and review is local-only now — the data
    // already lives in S and is persisted to localStorage on every change
    // (see the deep S watcher below), so there is nothing to send until the
    // single submit at review. canContinue already gates whether this can
    // even be reached.
    advanceStep() {
      const i = this.screenOrder.indexOf(this.screen)
      this.screen = this.screenOrder[i + 1]
    },
    async finishClaim() {
      this.submittingStep = true
      try {
        const { data } = await submitClaim({
          claimId: this.S.claimId,
          header: {
            regNo: this.S.reg.trim(),
            phoneNumber: this.S.phone.trim(),
          },
          body: toFullClaimBody(this.S, this.origin),
        })
        if (!data || !data.success) {
          this.showToast(
            (data && data.msg) ||
              'Could not submit your claim. Please try again.'
          )
          return
        }
        this.S.ref = data.data.claimReference
        clearDraft(this.draftKey)
        this.screen = 'done'
      } finally {
        this.submittingStep = false
      }
    },
  },
}
</script>

<style scoped>
.motor-claim {
  height: 100%;
}
</style>
